import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Trash2, Hexagon, ChevronLeft, ChevronRight, Check,
} from "lucide-react";
import { PatrolMap } from "@/components/PatrolMap";
import { LocationPermissionBanner } from "@/components/LocationPermissionBanner";
import { GeofenceWizardMap, AddressSearch } from "@/components/GeofenceWizard";

export const Route = createFileRoute("/_authenticated/geofences")({
  component: GeofencesPage,
  head: () => ({ meta: [{ title: "Geofences — MissionReady" }] }),
});

type Site = { id: string; name: string; latitude: number | null; longitude: number | null };
type Geofence = {
  id: string; name: string; kind: "inclusion" | "exclusion";
  center_lat: number; center_lng: number; radius_m: number; active: boolean; site_id: string | null;
};

function GeofencesPage() {
  const { currentOrg, currentRole } = useAuth();
  const canEdit = ["org_owner", "admin", "supervisor"].includes(currentRole ?? "");
  const [sites, setSites] = useState<Site[]>([]);
  const [fences, setFences] = useState<Geofence[]>([]);
  const [open, setOpen] = useState(false);
  const [prefillCenter, setPrefillCenter] = useState<{ lat: number; lng: number } | null>(null);

  const openWizardAt = (lat: number, lng: number) => {
    setPrefillCenter({ lat, lng });
    setOpen(true);
  };

  const load = async () => {
    if (!currentOrg) return;
    const [{ data: ss }, { data: gs }] = await Promise.all([
      supabase.from("sites").select("id,name,latitude,longitude").eq("organization_id", currentOrg.id),
      supabase.from("geofences").select("*").eq("organization_id", currentOrg.id).order("created_at", { ascending: false }),
    ]);
    setSites((ss as any) ?? []);
    setFences((gs as any) ?? []);
  };
  useEffect(() => { load(); }, [currentOrg?.id]);

  const toggleActive = async (g: Geofence, v: boolean) => {
    await supabase.from("geofences").update({ active: v }).eq("id", g.id);
    setFences((arr) => arr.map((x) => x.id === g.id ? { ...x, active: v } : x));
  };
  const remove = async (g: Geofence) => {
    if (!confirm(`Delete geofence "${g.name}"?`)) return;
    const { error } = await supabase.from("geofences").delete().eq("id", g.id);
    if (error) { toast.error(error.message); return; }
    setFences((arr) => arr.filter((x) => x.id !== g.id));
  };

  return (
    <div className="px-4 py-6 md:px-8 md:py-8">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-primary">Field Operations</p>
          <h1 className="font-display text-2xl font-bold md:text-3xl">Geofences</h1>
          <p className="text-sm text-muted-foreground">Inclusion zones trigger enter/exit events. Exclusion zones flag breaches.</p>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setPrefillCenter(null); }}>
            <DialogTrigger asChild>
              <Button size="sm" onClick={() => setPrefillCenter(null)}><Plus className="mr-1 h-4 w-4" /> New geofence</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <GeofenceWizard
                sites={sites}
                onClose={() => { setOpen(false); setPrefillCenter(null); }}
                onSaved={() => { setOpen(false); setPrefillCenter(null); load(); }}
                orgId={currentOrg?.id ?? ""}
                initialCenter={prefillCenter}
              />
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="mt-6"><LocationPermissionBanner /></div>

      {canEdit && (
        <div className="mb-3 mt-3 rounded-xl border border-[#C0C0C0]/20 bg-card p-3">
          <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            Quick add — search an address or tap the map to drop a zone
          </p>
          <AddressSearch onPick={(lat, lng) => openWizardAt(lat, lng)} />
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-[#C0C0C0]/20 bg-card lg:col-span-2 overflow-hidden">
          <PatrolMap
            geofences={fences}
            height={460}
            fitTo="geofences"
            showUserLocation
            enableStyleToggle
            defaultStyle="dark"
            onMapClick={canEdit ? openWizardAt : undefined}
          />
        </div>
        <div className="rounded-xl border border-[#C0C0C0]/20 bg-card">
          <div className="border-b border-[#C0C0C0]/20 px-4 py-3">
            <h2 className="font-display text-base font-semibold">All zones ({fences.length})</h2>
          </div>
          {fences.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">No geofences yet.</div>
          ) : (
            <ul className="max-h-[460px] divide-y divide-[#C0C0C0]/10 overflow-auto">
              {fences.map((g) => (
                <li key={g.id} className="flex items-start gap-3 px-4 py-3">
                  <div className={`mt-0.5 flex h-8 w-8 items-center justify-center rounded-md ${g.kind === "exclusion" ? "bg-destructive/15 text-destructive" : "bg-primary/15 text-primary"}`}>
                    <Hexagon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{g.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {g.kind} · {Math.round(g.radius_m)} m · {Number(g.center_lat).toFixed(4)}, {Number(g.center_lng).toFixed(4)}
                    </div>
                  </div>
                  {canEdit && (
                    <div className="flex items-center gap-2">
                      <Switch checked={g.active} onCheckedChange={(v) => toggleActive(g, v)} />
                      <Button size="icon" variant="ghost" onClick={() => remove(g)} aria-label={`Delete geofence ${g.name}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

/* ================================================================ */
/* Wizard                                                           */
/* ================================================================ */

function GeofenceWizard({
  sites, orgId, onClose, onSaved, initialCenter,
}: {
  sites: Site[];
  orgId: string;
  onClose: () => void;
  onSaved: () => void;
  initialCenter?: { lat: number; lng: number } | null;
}) {
  const [step, setStep] = useState<1 | 2 | 3>(initialCenter ? 2 : 1);
  const [siteId, setSiteId] = useState<string>("");
  const [name, setName] = useState("");
  const [kind, setKind] = useState<"inclusion" | "exclusion">("inclusion");
  const [center, setCenter] = useState<{ lat: number; lng: number } | null>(initialCenter ?? null);
  const [radius, setRadius] = useState(230);
  const [busy, setBusy] = useState(false);

  // When a site is picked, prefill name + center if site has coords
  useEffect(() => {
    if (!siteId) return;
    const s = sites.find((x) => x.id === siteId);
    if (s) {
      if (!name) setName(`${s.name} perimeter`);
      if (s.latitude != null && s.longitude != null && !center) {
        setCenter({ lat: Number(s.latitude), lng: Number(s.longitude) });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  const siteName = useMemo(
    () => sites.find((s) => s.id === siteId)?.name ?? "Unassigned",
    [sites, siteId],
  );

  const save = async () => {
    if (!orgId) return;
    if (!name.trim()) return toast.error("Give the geofence a name");
    if (!center) return toast.error("Drop a pin on the map first");
    setBusy(true);
    const { error } = await supabase.from("geofences").insert({
      organization_id: orgId,
      site_id: siteId || null,
      name: name.trim(),
      kind,
      center_lat: center.lat,
      center_lng: center.lng,
      radius_m: Math.round(radius),
      active: true,
    });
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success(
      `Geofence saved — ${siteName} · ${Math.round(radius)}m at ${center.lat.toFixed(5)}, ${center.lng.toFixed(5)}`,
    );
    onSaved();
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>New geofence</DialogTitle>
      </DialogHeader>

      {/* Site selector + step indicator */}
      <div className="grid gap-3">
        <div className="grid gap-1.5">
          <Label>Site</Label>
          <Select value={siteId} onValueChange={setSiteId}>
            <SelectTrigger aria-label="Site"><SelectValue placeholder="Associate with a site (optional)" /></SelectTrigger>
            <SelectContent>
              {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <Stepper step={step} />

        {step === 1 && (
          <div className="space-y-4">
            <div className="grid gap-1.5">
              <Label htmlFor="gf-name">Name</Label>
              <Input
                id="gf-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Main perimeter"
              />
            </div>
            <div className="grid gap-1.5">
              <Label>Type</Label>
              <Select value={kind} onValueChange={(v) => setKind(v as any)}>
                <SelectTrigger aria-label="Type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="inclusion">Inclusion (must stay inside)</SelectItem>
                  <SelectItem value="exclusion">Exclusion (no-go zone)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <AddressSearch
              onPick={(lat, lng) => {
                setCenter({ lat, lng });
                setStep(2);
              }}
            />
            <p className="text-xs text-muted-foreground">
              Search an address or paste coordinates, then tap <span className="text-foreground">Find</span>.
            </p>
          </div>
        )}

        {step === 2 && (
          <GeofenceWizardMap
            center={center}
            radius={radius}
            onChange={({ lat, lng, radius: r }) => {
              setCenter({ lat, lng });
              setRadius(r);
            }}
          />
        )}

        {step === 3 && (
          <div className="space-y-3 rounded-lg border border-[#C0C0C0]/20 bg-card p-4">
            <Row label="Name" value={name || "—"} />
            <Row label="Site" value={siteName} />
            <Row label="Type" value={kind} />
            <Row
              label="Center"
              value={center ? `${center.lat.toFixed(5)}, ${center.lng.toFixed(5)}` : "—"}
              mono
            />
            <Row label="Radius" value={`${Math.round(radius)} m`} mono />
          </div>
        )}
      </div>

      <DialogFooter className="mt-2 flex !justify-between gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => (step === 1 ? onClose() : setStep((s) => (s - 1) as 1 | 2 | 3))}
          disabled={busy}
        >
          <ChevronLeft className="mr-1 h-4 w-4" />
          {step === 1 ? "Cancel" : "Back"}
        </Button>

        {step < 3 ? (
          <Button
            type="button"
            onClick={() => setStep((s) => (s + 1) as 1 | 2 | 3)}
            disabled={step === 1 ? !center : !center}
          >
            Next <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        ) : (
          <Button type="button" onClick={save} disabled={busy}>
            <Check className="mr-1 h-4 w-4" />
            {busy ? "Saving…" : "Save Geofence"}
          </Button>
        )}
      </DialogFooter>
    </>
  );
}

function Stepper({ step }: { step: 1 | 2 | 3 }) {
  const steps = [
    { n: 1, label: "Address" },
    { n: 2, label: "Map editor" },
    { n: 3, label: "Save" },
  ];
  return (
    <ol className="flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-[#C0C0C0]">
      {steps.map((s, i) => (
        <li key={s.n} className="flex items-center gap-2">
          <span
            className={`flex h-6 w-6 items-center justify-center rounded-full border text-[11px] font-bold ${
              s.n === step
                ? "border-primary bg-primary text-white"
                : s.n < step
                  ? "border-primary text-primary"
                  : "border-[#C0C0C0]/40 text-[#C0C0C0]"
            }`}
          >
            {s.n}
          </span>
          <span className={s.n === step ? "text-foreground" : ""}>{s.label}</span>
          {i < steps.length - 1 && <span className="mx-1 h-px w-6 bg-[#C0C0C0]/30" />}
        </li>
      ))}
    </ol>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#C0C0C0]">{label}</span>
      <span className={mono ? "font-mono text-foreground" : "text-foreground capitalize"}>{value}</span>
    </div>
  );
}
